import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ArrowRight, BadgeCheck, Wallet, ShieldCheck, Handshake, Briefcase, Clock, Globe } from "lucide-react";

const ethos = [
  {
    icon: BadgeCheck,
    title: "Best-in-class delivery",
    desc: "We hold every engagement to the standards we learned inside global enterprises — rigorous, reviewed, and consistent, so the quality never depends on the day.",
  },
  {
    icon: Wallet,
    title: "Real cost efficiency",
    desc: "A skilled offshore finance team lets you cut the cost of bookkeeping and tax operations substantially, while freeing your people to focus on growth.",
  },
  {
    icon: ShieldCheck,
    title: "Security and confidentiality",
    desc: "Your financial data is handled through secure systems and controlled access, with enterprise-grade confidentiality at every stage of the work.",
  },
  {
    icon: Handshake,
    title: "A genuine partnership",
    desc: "We measure success by your outcomes, not hours billed — acting as a dependable extension of your team for the long term.",
  },
];

const credentials = [
  { icon: Briefcase, label: "Big 4 pedigree" },
  { icon: Clock, label: "10+ years of experience" },
  { icon: Globe, label: "Serving global enterprises" },
];

export default function AboutPage() {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  return (
    <main data-testid="about-page" className="relative bg-white">
      <Navbar />

      {/* intro */}
      <section className="pt-[88px] bg-gradient-to-br from-[#F7F8FA] to-[#ECEEF2]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 md:pt-24 pb-16 md:pb-20">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="label-tech !text-[#3B37E5] mb-6"
          >
            About Pikosky Partners
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display text-[2.75rem] sm:text-6xl lg:text-[4.25rem] leading-[0.97] text-[#0A0A0A] max-w-[20ch]"
          >
            Built on craft, trust, and a real stake in your success.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.12 }}
            className="mt-8 font-premium italic text-2xl md:text-3xl text-[#14213D] max-w-3xl leading-snug"
          >
            We give growing businesses the calibre of finance function usually reserved for large enterprises — delivered with precision, security, and meaningful cost savings.
          </motion.p>
        </div>
      </section>

      {/* ethos */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <p className="label-tech !text-[#3B37E5] mb-6">Our ethos</p>
          <h2 className="font-display text-4xl md:text-6xl leading-[0.98] text-[#0A0A0A] max-w-[16ch] mb-16">
            What we stand for.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {ethos.map((e, i) => {
              const Icon = e.icon;
              return (
                <motion.div
                  key={e.title}
                  data-testid={`ethos-${i}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.55, delay: (i % 2) * 0.08 }}
                  className="flex gap-5 p-8 rounded-lg border border-[#EAECF0] bg-[#FBFBFC]"
                >
                  <span className="shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-[#14213D]">
                    <Icon size={20} className="text-white" strokeWidth={2} />
                  </span>
                  <div>
                    <h3 className="font-display text-xl text-[#0A0A0A] mb-2">{e.title}</h3>
                    <p className="text-[#52525B] leading-relaxed">{e.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* founders */}
      <section id="founders" data-testid="founders-section" className="bg-[#14213D] text-white py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="label-tech !text-[#8B93B8] mb-6">The founders</p>
            <h2 className="font-display text-4xl md:text-5xl leading-[0.98]">
              The people behind the practice.
            </h2>
            <div className="mt-10 space-y-4">
              {credentials.map((c) => {
                const Icon = c.icon;
                return (
                  <div key={c.label} className="flex items-center gap-3">
                    <span className="flex items-center justify-center h-9 w-9 rounded-full bg-white/10">
                      <Icon size={16} className="text-white" />
                    </span>
                    <span className="text-[#AEB6CE]">{c.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-8 space-y-6 text-lg text-[#C6CEDA] leading-relaxed max-w-2xl">
            <p>
              Pikosky Partners was founded by two finance professionals who spent over a decade inside Big 4 firms, leading accounting and tax engagements for some of the world's most demanding enterprises.
            </p>
            <p>
              After years of building world-class processes for others, they set out to create something of their own — a venture that brings the same rigour, technology, and discipline to businesses everywhere, at a fraction of the traditional cost.
            </p>
            <p>
              Their work is driven by a simple belief: exceptional financial operations should create real, lasting value for every client. That entrepreneurial mindset shapes how we hire, how we review our work, and how we show up for the businesses that trust us — as partners invested in their long-term growth.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <h2 className="font-display text-4xl md:text-6xl leading-[0.98] text-[#0A0A0A] max-w-[16ch]">
            Let's build your finance function together.
          </h2>
          <button
            data-testid="about-cta"
            onClick={() => navigate("/#contact")}
            className="group inline-flex items-center gap-2 label-tech !text-[#3B37E5] hover:!text-[#2A26D6] border-b border-[#3B37E5]/40 pb-2 transition-colors self-start lg:self-auto"
          >
            Start a conversation
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </section>

      <Footer />
    </main>
  );
}
